import { secondsOf, type ValueField } from './durationField';

/**
 * Seat time: how long a seat stays with a device that has stopped calling back before another
 * device may take it. The license file's value is the least a license may have and a week the
 * most, and the administrator may set anything in between (`backend/doc/CONFIGURATION.md`
 * section 9). The same bounds the server enforces, so most mistakes are caught before the round
 * trip; the server stays the authority.
 */
export const MAX_SEAT_TIME_SECONDS = 7 * 86400;

/**
 * What the seat time dialog says about the value in its field, or undefined when it can be saved.
 * `floor` is the license file's seat time. Decimals are taken and rounded to whole seconds, as the
 * offline credit dialog does.
 */
export const seatTimeProblem = (field: ValueField, floor: number): string | undefined => {
  if (!field.own) return undefined;

  const seconds = secondsOf(field);

  if (seconds === undefined) return `Enter a seat time of at least ${floor.formatDuration()}.`;
  if (seconds < floor) return `The seat time cannot be shorter than the license file's ${floor.formatDuration()}.`;
  if (seconds > MAX_SEAT_TIME_SECONDS) return 'The seat time cannot be longer than 7 days.';

  return undefined;
};

/**
 * What the dialog sends: the seat time in whole seconds, or null to go back to the file's. Never
 * null for a field that is ticked but holds no number (Save is disabled then), so a slip cannot
 * clear the administrator's value.
 */
export const seatTimeToSave = (field: ValueField): number | null | undefined => field.own ? secondsOf(field) : null;
